import { createResponseSchema } from '@mint-sdk/api-core';
import { z } from 'zod';

import { FilterMappingSchema } from './filterMappings.js';
import { RowLevelEnum } from './rowLevel.js';
import { TrackerMappingSchema } from './trackerMappings.js';

export const TaxonomyQuerySchema = z.object({
  settingsId: z.string(),
  startRow: z.number(),
  endRow: z.number(),
  groupKey: z.array(z.string()),
  platformName: z.string().nullish(),
});

export const ParentSchema = z.object({
  levelId: z.string(),
  name: z.string(),
  label: z.string(),
});

export const GroupingTypeEnum = z.enum(['campaign', 'ad_group', 'ad']);

export const GroupingSchema = z.object({
  id: z.string(),
  settingsId: z.string(),
  platformId: z.string(),
  platformName: z.string(),
  mediaRowId: z.string(),
  type: GroupingTypeEnum,
  labels: z.record(z.string(), z.string()),
});

export const TaxonomyItemBaseSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  userDefinedId: z.string().nullable().optional(),
  name: z.string().nullable(),
  label: z.string().nullable(),
  rowType: RowLevelEnum.nullable(),
  hasChildren: z.boolean(),
  parents: z.array(ParentSchema).nullable(),
  subtype: z.string().nullable(),
  status: z.string().nullable(),
  grouping: GroupingSchema.nullable(),
  hasDuplicateTrackers: z.boolean().optional(),
  isAutomappingEnabled: z.boolean().optional(),
  hasDuplicateCampaignMappings: z.boolean().optional(),
});

export const FilterLevelSchema = TaxonomyItemBaseSchema.extend({
  rowType: z.union([
    z.literal('activity'),
    z.literal('platform'),
    z.literal('media_row'),
  ]),
  filterMappings: z.array(FilterMappingSchema),
});

export const TrackerLevelSchema = TaxonomyItemBaseSchema.extend({
  rowType: z.union([
    z.literal('campaign'),
    z.literal('ad_group'),
    z.literal('ad'),
  ]),
  trackerMappings: z.array(TrackerMappingSchema).nullish(), // TODO: remove nullish when BE will be ready
  filterMappings: z.array(FilterMappingSchema).nullish(),
  isCampaignMappingSupported: z.boolean().optional(),
});

export const TaxonomyItemSchema = z.discriminatedUnion('rowType', [
  FilterLevelSchema,
  TrackerLevelSchema,
]);

export const TaxonomyResponseSchema = createResponseSchema(TaxonomyItemSchema);

export type FilterLevelTaxonomyItem = z.infer<typeof FilterLevelSchema>;
export type TrackerLevelTaxonomyItem = z.infer<typeof TrackerLevelSchema>;
